/**
 * Coverage view — explicit, prominent display of what the system does NOT
 * cover: Tier-4 state law (not in the federal source), DIN full-text
 * obligations (license-blocked, metadata only) and anything with
 * coverageStatus pending/notCovered.
 *
 * This is a liability requirement, not UX polish: the system shows what it
 * knows and flags what it doesn't — it must never create a false impression
 * of completeness.
 */

import { useEffect, useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { ArrowDown, ArrowUp, ArrowUpDown } from "lucide-react";
import type { Column, ColumnDef } from "@tanstack/react-table";

import Section from "../../components/Section";
import { useModalContext } from "../../components/ModalProvider";
import { useLegalAdministrationAPI } from "../../LegalAdministrationAPI";
import { CoverageStatusBadge, WrappedText } from "./legalDisplay";

import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { DataTable } from "@/components/ui/data-table";
import { Skeleton } from "@/components/ui/skeleton";

interface Properties {
  refreshSignal: number;
}

/** Klickbarer Spaltenkopf — ersetzt die Sortierung der abgelösten Tabelle. */
function SortableHeader({ column, children }: { column: Column<any, unknown>; children: ReactNode }) {
  const sorted = column.getIsSorted();

  return (
    <Button
      variant="ghost"
      size="sm"
      className="-ml-3 h-8 gap-1 px-3 font-bold text-foreground"
      onClick={() => column.toggleSorting(sorted === "asc")}
    >
      {children}
      {sorted === "asc" ? (
        <ArrowUp aria-hidden className="h-3.5 w-3.5" />
      ) : sorted === "desc" ? (
        <ArrowDown aria-hidden className="h-3.5 w-3.5" />
      ) : (
        <ArrowUpDown aria-hidden className="h-3.5 w-3.5 opacity-50" />
      )}
    </Button>
  );
}

export default function CoveragePanel(properties: Properties) {
  const { t } = useTranslation();
  const { pushError } = useModalContext() as any;
  const legalAPI = useLegalAdministrationAPI();

  const [gaps, setGaps] = useState<any[]>([]);
  const [coveredCount, setCoveredCount] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        const result = await legalAPI.getCoverage();

        setGaps(result.gaps || []);
        setCoveredCount(result.coveredCount || 0);
      } catch (error) {
        pushError(error);
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [properties.refreshSignal]);

  const columns: ColumnDef<any>[] = [
    {
      id: "shortName",
      accessorFn: (regulation: any) => regulation.shortName || regulation.eliId,
      header: ({ column }) => (
        <SortableHeader column={column}>{t("operatorResponsibilityPanel.shortName")}</SortableHeader>
      ),
      size: 180,
    },
    {
      id: "title",
      accessorFn: (regulation: any) => regulation.title,
      header: t("operatorResponsibilityPanel.regulationTitle"),
      size: 280,
      enableSorting: false,
      // maxWidth auf dem Blockinhalt begrenzt die Spalte, ohne den Text zu kürzen.
      cell: ({ row }) => <WrappedText text={row.original.title} maxWidth={280} fontSize={12} />,
    },
    {
      id: "level",
      accessorFn: (regulation: any) =>
        t(`operatorResponsibilityPanel.levelValue.${regulation.level || "unknown"}`),
      header: ({ column }) => (
        <SortableHeader column={column}>{t("operatorResponsibilityPanel.level")}</SortableHeader>
      ),
      size: 100,
    },
    {
      id: "coverage",
      accessorFn: (regulation: any) => regulation.coverageStatus,
      header: ({ column }) => (
        <SortableHeader column={column}>{t("operatorResponsibilityPanel.coverage")}</SortableHeader>
      ),
      size: 140,
      cell: ({ row }) => <CoverageStatusBadge status={row.original.coverageStatus} />,
    },
    {
      id: "coverageNote",
      accessorFn: (regulation: any) => regulation.coverageNote || "—",
      header: t("operatorResponsibilityPanel.coverageNote"),
      enableSorting: false,
      cell: ({ row }) => <WrappedText text={row.original.coverageNote} maxWidth={420} />,
    },
  ];

  return (
    <div className="flex w-full min-w-0 max-w-[1400px] flex-col gap-4">
      {/* Der Haftungshinweis steht bewusst über allem anderen: Was das System
          nicht abdeckt, darf nicht unter der Tabelle verschwinden. */}
      <Alert variant="destructive">
        <AlertTitle>{t("operatorResponsibilityPanel.coverageDisclaimerTitle")}</AlertTitle>
        <AlertDescription className="min-w-0 break-words">
          {t("operatorResponsibilityPanel.coverageDisclaimer")}
        </AlertDescription>
      </Alert>

      <p className="min-w-0 break-words text-sm text-muted-foreground">
        {t("operatorResponsibilityPanel.coverageCounts", { covered: coveredCount, gaps: gaps.length })}
      </p>

      <Section title={t("operatorResponsibilityPanel.coverageGaps")}>
        {loading ? (
          <div className="flex min-w-0 flex-col gap-2">
            <Skeleton className="h-6 w-[220px]" />
            <Skeleton className="h-[200px] w-full" />
          </div>
        ) : (
          <div className="min-w-0 overflow-x-auto">
            <DataTable
              columns={columns}
              data={gaps}
              pageSize={10}
              previousAriaLabel={t("operatorResponsibilityPanel.previousPage")}
              nextAriaLabel={t("operatorResponsibilityPanel.nextPage")}
              emptyMessage={
                // Keine Lücke ist ein Befund, kein leerer Datensatz — der Text
                // sagt, worauf sich das bezieht.
                <span className="min-w-0 break-words">
                  {t("operatorResponsibilityPanel.noCoverageGaps")}
                </span>
              }
            />
          </div>
        )}
      </Section>
    </div>
  );
}
